import { ReactNode, useState, type RefCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import styles from "@/components/lms/lmsFileDropzone.module.css";

interface Props {
  label?: string;
  icon?: ReactNode;
  helperText?: string;
  emptyStateText: string;
  acceptedFileTypesText?: string;
  value: File | null;
  onChange: (file: File | null) => void;
  accept: Record<string, string[]>;
  rejectionMessage?: string;
  errorMessage?: string;
  mandatory?: boolean;
  clearLabel?: string;
  dataTestId?: string;
  inputDataTestId?: string;
  disabled?: boolean;
  validationTargetRef?: RefCallback<HTMLElement>;
}

export default function LmsFileDropzone({
  label,
  icon,
  helperText,
  emptyStateText,
  acceptedFileTypesText,
  value,
  onChange,
  accept,
  rejectionMessage,
  errorMessage,
  mandatory,
  clearLabel,
  dataTestId,
  inputDataTestId,
  disabled = false,
  validationTargetRef,
}: Props) {
  const { t } = useTranslation();
  const [localError, setLocalError] = useState("");

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    multiple: false,
    accept,
    disabled,
    onDropAccepted: (acceptedFiles) => {
      onChange(acceptedFiles[0] ?? null);
      setLocalError("");
    },
    onDropRejected: () => {
      setLocalError(rejectionMessage ?? t("upload_failed"));
    },
  });

  return (
    <div ref={validationTargetRef} data-testid={dataTestId}>
      <div className={styles.fileSectionHeader}>
        <Typography>
          {label}
          {mandatory && <span className={styles.requiredAsterisk}>*</span>}
        </Typography>
        {value && (
          <button
            type="button"
            className={styles.clearFileButton}
            disabled={disabled}
            onClick={() => {
              onChange(null);
              setLocalError("");
            }}
          >
            {clearLabel ?? t("remove")}
          </button>
        )}
      </div>
      <div
        {...getRootProps()}
        className={`${styles.dropzone} ${
          isDragActive ? styles.dropzoneActive : ""
        } ${disabled ? styles.dropzoneDisabled : ""}`}
        aria-disabled={disabled}
      >
        <input {...getInputProps()} data-testid={inputDataTestId} />
        {icon ?? null}
        {value ? (
          <>
            <Typography className={styles.selectedFileName}>{value.name}</Typography>
            {helperText && (
              <Typography className={styles.dropzoneHelper}>{helperText}</Typography>
            )}
          </>
        ) : (
          <>
            <Typography className={styles.dropzoneTitle}>{emptyStateText}</Typography>
            {acceptedFileTypesText && (
              <Typography className={styles.dropzoneHelper}>
                {acceptedFileTypesText}
              </Typography>
            )}
          </>
        )}
      </div>
      {(localError || errorMessage) && (
        <div className={styles.fileErrorText}>
          {localError || errorMessage}
        </div>
      )}
    </div>
  );
}
